import { useEffect, useState } from 'preact/hooks';
import { createSchedule, updateSchedule } from '../lib/api';
import { active } from '../store/global';
import type { Schedule } from '../types/agno-api';

interface Props {
  open: boolean;
  initial: Schedule | null; // null = criação; objeto = edição
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  cron_expr: string;
  endpoint: string;
  method: string;
  payload: string; // JSON serializado pra edição em textarea
  timezone: string;
  description: string;
  max_retries: string;
  retry_delay_seconds: string;
}

const CRON_PRESETS: Array<{ label: string; expr: string }> = [
  { label: 'A cada 15 min', expr: '*/15 * * * *' },
  { label: 'De hora em hora', expr: '0 * * * *' },
  { label: 'Todo dia às 8h', expr: '0 8 * * *' },
  { label: 'Dias úteis às 9h', expr: '0 9 * * 1-5' },
  { label: 'Segunda às 9h', expr: '0 9 * * 1' },
];

function defaultEndpoint(): string {
  const a = active.value;
  if (!a) return '';
  return a.isTeam ? `/teams/${a.targetId}/runs` : `/agents/${a.targetId}/runs`;
}

function emptyForm(): FormState {
  return {
    name: '',
    cron_expr: '',
    endpoint: defaultEndpoint(),
    method: 'POST',
    payload: '{\n  "message": "..."\n}',
    timezone: 'America/Sao_Paulo',
    description: '',
    max_retries: '0',
    retry_delay_seconds: '30',
  };
}

function formFromSchedule(s: Schedule): FormState {
  return {
    name: s.name || '',
    cron_expr: s.cron_expr || s.cron || '',
    endpoint: s.endpoint || defaultEndpoint(),
    method: s.method || 'POST',
    payload: s.payload ? JSON.stringify(s.payload, null, 2) : '{}',
    timezone: s.timezone || 'America/Sao_Paulo',
    description: s.description || '',
    max_retries: String(s.max_retries ?? 0),
    retry_delay_seconds: String(s.retry_delay_seconds ?? 30),
  };
}

export function ScheduleEditor({ open, initial, onClose, onSaved }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(initial ? formFromSchedule(initial) : emptyForm());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, initial, onClose]);

  if (!open) return null;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) return setError('Informe o nome do agendamento.');
    if (!form.cron_expr.trim()) return setError('Informe a expressão cron.');
    if (!form.endpoint.trim()) return setError('Informe o endpoint.');

    let parsedPayload: Record<string, unknown> | undefined;
    if (form.payload.trim()) {
      try {
        parsedPayload = JSON.parse(form.payload);
      } catch (e) {
        return setError(`O payload não é um JSON válido: ${(e as Error).message}`);
      }
    }

    const body: Partial<Schedule> = {
      name: form.name.trim(),
      cron_expr: form.cron_expr.trim(),
      endpoint: form.endpoint.trim(),
      method: form.method,
      payload: parsedPayload,
      timezone: form.timezone.trim() || 'UTC',
      description: form.description.trim() || undefined,
      max_retries: Number(form.max_retries) || 0,
      retry_delay_seconds: Number(form.retry_delay_seconds) || 30,
    };

    setSaving(true);
    try {
      if (initial) await updateSchedule(initial.id, body);
      else await createSchedule(body);
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        class="bg-background border border-border rounded-lg shadow-sm w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="h-12 px-4 sm:px-5 flex items-center border-b border-border shrink-0">
          <i class="ph ph-clock text-base mr-2 text-muted-foreground" />
          <h2 class="text-sm font-medium">
            {initial ? 'Editar agendamento' : 'Novo agendamento'}
          </h2>
          <button
            class="ml-auto -mr-1.5 inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={onClose}
            aria-label="Fechar"
          >
            <i class="ph ph-x text-base" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
          <Field label="Nome" required>
            <input
              class="input"
              value={form.name}
              onInput={(e) => set('name', (e.target as HTMLInputElement).value)}
              placeholder="ex: resumo-diario"
            />
          </Field>

          <Field label="Descrição">
            <input
              class="input"
              value={form.description}
              onInput={(e) => set('description', (e.target as HTMLInputElement).value)}
              placeholder="Para que serve esse agendamento"
            />
          </Field>

          <Field label="Cron" required hint="formato padrão: min hora dia mês dia-semana">
            <div class="flex flex-col gap-1.5">
              <input
                class="input font-mono"
                value={form.cron_expr}
                onInput={(e) => set('cron_expr', (e.target as HTMLInputElement).value)}
                placeholder="0 8 * * 1-5"
              />
              <div class="flex flex-wrap gap-1">
                {CRON_PRESETS.map((p) => (
                  <button
                    key={p.expr}
                    class="text-xs px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    onClick={() => set('cron_expr', p.expr)}
                    type="button"
                    title={p.expr}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </Field>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Endpoint" required>
              <input
                class="input font-mono"
                value={form.endpoint}
                onInput={(e) => set('endpoint', (e.target as HTMLInputElement).value)}
                placeholder="/teams/<id>/runs"
              />
            </Field>
            <Field label="Método">
              <select
                class="input"
                value={form.method}
                onChange={(e) => set('method', (e.target as HTMLSelectElement).value)}
              >
                <option value="POST">POST</option>
                <option value="GET">GET</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </Field>
          </div>

          <Field label="Payload (JSON)" hint="Corpo que vai pro endpoint a cada disparo">
            <textarea
              class="input font-mono text-xs min-h-[120px] resize-y"
              value={form.payload}
              onInput={(e) => set('payload', (e.target as HTMLTextAreaElement).value)}
              spellcheck={false}
            />
          </Field>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Timezone">
              <input
                class="input"
                value={form.timezone}
                onInput={(e) => set('timezone', (e.target as HTMLInputElement).value)}
                placeholder="America/Sao_Paulo"
              />
            </Field>
            <Field label="Max retries">
              <input
                class="input"
                type="number"
                min="0"
                value={form.max_retries}
                onInput={(e) => set('max_retries', (e.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Retry delay (s)">
              <input
                class="input"
                type="number"
                min="0"
                value={form.retry_delay_seconds}
                onInput={(e) => set('retry_delay_seconds', (e.target as HTMLInputElement).value)}
              />
            </Field>
          </div>

          {error && (
            <div class="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 text-foreground px-4 py-3 text-sm">
              <i class="ph ph-warning-circle text-base text-destructive shrink-0 mt-px" />
              <span class="break-words">{error}</span>
            </div>
          )}
        </div>

        <div class="px-4 sm:px-5 py-3 border-t border-border flex items-center justify-end gap-2 shrink-0">
          <button
            class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
            onClick={onClose}
            disabled={saving}
            type="button"
          >
            Cancelar
          </button>
          <button
            class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
            onClick={submit}
            disabled={saving}
            type="button"
          >
            {saving ? 'Salvando…' : initial ? 'Salvar alterações' : 'Criar agendamento'}
          </button>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          height: 2rem;
          padding: 0.25rem 0.75rem;
          border-radius: var(--radius-md);
          border: 1px solid var(--color-input);
          background: transparent;
          font-size: 0.875rem;
          box-shadow: 0 1px 2px 0 color-mix(in oklab, var(--color-foreground) 5%, transparent);
          outline: none;
        }
        .input::placeholder { color: var(--color-muted-foreground); }
        .input:focus-visible {
          border-color: var(--color-ring);
          box-shadow: 0 0 0 3px color-mix(in oklab, var(--color-ring) 50%, transparent);
        }
        .input:disabled { opacity: 0.5; }
        textarea.input { height: auto; padding: 0.5rem 0.75rem; line-height: 1.4; }
        select.input { padding-right: 1.5rem; }
      `}</style>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: preact.ComponentChildren;
}) {
  return (
    <label class="block">
      <div class="text-xs font-medium text-foreground mb-1">
        {label}
        {required && <span class="text-destructive ml-0.5">*</span>}
      </div>
      {children}
      {hint && <div class="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </label>
  );
}
